
import { DATATABLES_SELECTOR, RTC_IFRAME_ID } from './constants';
import { calculateAllConsultationFields } from './consultations';
import { confirmLoadLocalDraft, removeAllLocalDrafts, saveDraft } from './draft';
import { handleProjectChange, initializeSelect2 } from './forms';
import { setupBirthplace } from './patients';
import { pushToReactSnackbar, store } from './reactTreeMounting';
import { initializeReportTypeSelection, setupReportTenantSelectAll } from './reports';
import { selectManagedExpertById } from 'FRONTEND/reducers/expertSlice';
import { APPOINTMENT_DEFAULT_DURATION, VIDEO_CONSULTATION, VIDEO_SPECIALIST_ASSESSMENT, OTHER } from 'FRONTEND/utils/constants';
import i18n from 'FRONTEND/utils/i18n';
import { getDurationSettingKey } from 'FRONTEND/utils/stringFormatting';

/** ****************************** */
/** ***** Logout/Session end ***** */
/** ****************************** */

async function rtcLogout() {
  const rtcIframe = document.getElementById(RTC_IFRAME_ID);
  if (rtcIframe) {
    rtcIframe.contentWindow.postMessage({ type: 'logout' }, '*');
  }
}

export function logOut() {
  /**
   * Log out: clear localStorage drafts, logout from RTC, then log out from the Cloud.
   */
  window.isLoggingOut = true;
  const logoutUrl = $('.user-logout').attr('href');

  saveDraft().finally(() => {
    removeAllLocalDrafts({});
    rtcLogout().finally(() => {
      window.location.href = logoutUrl;
    });
  });
}

$(document).ajaxError(function sessionEnd(event, jqXHR, ajaxSettings) {
  if (jqXHR.status === 401) {
    logOut();
  } else {
    if (jqXHR.statusText !== 'abort') {
      pushToReactSnackbar({ variant: 'error', message: i18n._('An error occurred while processing your request.') });
    }

    if (jqXHR.status !== 0 || !['abort', 'timeout'].includes(jqXHR.statusText)) {
      const url = new URL(ajaxSettings.url, 'https://placeholder');
      throw Error(`${ajaxSettings.type} ${url.pathname} ${jqXHR.status} ${jqXHR.statusText}`);
    }
  }
});

$(document).on('click', '.user-logout', function logout(event) {
  event.preventDefault();
  logOut();
});

/** ****************************** */
/** *********** Init ************* */
/** ****************************** */

document.addEventListener('DOMContentLoaded', () => {
  preparePageReady(true);

  setTimeout(() => {
    const navBarEl = document.getElementById('react_navBar');
    if (navBarEl) {
      const navBarChild = navBarEl.firstChild;
      if (navBarChild && navBarChild.id === 'navBar-fallback') {
        navBarChild.style.display = 'block';
      }
    }
  }, 5000);
});

// Called both on inital DOM load and pjax.
function preparePageReady(shouldConfirmLocalDraft) {
  // Delegated so tooltips also work on dynamically rendered content (datatables redraw their rows on
  // every sort/search/page change, which would unbind tooltips attached directly to the elements).
  // The hide delay gives the pointer time to travel from the trigger into the tooltip, which is what
  // makes a long one scrollable (see 'keepTooltipOpen' below).
  $(document.body).tooltip({
    selector: '[data-toggle=tooltip]',
    container: 'body',
    placement: 'auto',
    delay: { show: 0, hide: 300 },
  });

  // Load localStorage draft if there is one and if we are in a consultation read or creation page.
  if (shouldConfirmLocalDraft) {
    confirmLoadLocalDraft();
  }
  if ($('#consultations-draft')) {
    calculateAllConsultationFields();
  }

  initializeSelect2();
  setupBirthplace();
  setupReportTenantSelectAll();
  initializeReportTypeSelection();
  handleProjectChange();
}

// Tooltips are appended to <body>, so they outlive a trigger that sits inside the pjax container:
// clicking one (e.g. a datatable cell linking to another page) removes the trigger without ever
// firing 'mouseleave', leaving the tooltip stuck on screen. Hide it while the trigger still exists.
// Registered at module scope, as 'preparePageReady' runs again on every pjax navigation and 'on()'
// would stack a new handler each time.
$(document).on('click', '[data-toggle=tooltip]', function hideTooltipOnClick() {
  $(this).tooltip('hide');
});

const getTooltipInstance = (tip) => {
  /**
   * Find the tooltip instance owning a '.tooltip' element. Bootstrap only links the two while shown,
   * by pointing the trigger's 'aria-describedby' at the tooltip's generated id.
   */
  return $(`[aria-describedby="${tip.id}"]`).data('bs.tooltip');
};

// A note longer than the tooltip's max-height scrolls, but only if the tooltip survives the pointer
// leaving its trigger. Bootstrap schedules the hide and then checks 'hoverState' before running it,
// so putting the state back to 'in' cancels it; leaving the tooltip goes through the normal path.
$(document).on('mouseenter', '.tooltip', function keepTooltipOpen() {
  const tooltip = getTooltipInstance(this);
  if (tooltip) {
    clearTimeout(tooltip.timeout);
    tooltip.hoverState = 'in';
  }
});

$(document).on('mouseleave', '.tooltip', function hideTooltipOnLeave() {
  const tooltip = getTooltipInstance(this);
  if (tooltip) {
    tooltip.leave(tooltip);
  }
});

export function rtaSettingsIframeInit(userSettings) {
  /**
   * Sending the Cloud settings to the RTA settings iframe.
   */
  const rtaSettingsIframe = document.getElementById('user_settings-iframe');
  if (rtaSettingsIframe) {
    rtaSettingsIframe.contentWindow.postMessage({ type: 'ping_for_readiness' }, '*');

    const onMessage = function(event) {
      if (!event.data || !event.data.type) {
        return;
      }
      if (event.data.type === 'rta_ready' && rtaSettingsIframe) {
        rtaSettingsIframe.contentWindow.postMessage({
          type: 'cloud_settings',
          teleconsultation_duration: userSettings?.[getDurationSettingKey(VIDEO_CONSULTATION)] || APPOINTMENT_DEFAULT_DURATION,
          telestaff_duration: userSettings?.[getDurationSettingKey(VIDEO_SPECIALIST_ASSESSMENT)] || APPOINTMENT_DEFAULT_DURATION,
          other_appointment_duration: userSettings?.[getDurationSettingKey(OTHER)] || APPOINTMENT_DEFAULT_DURATION,
        }, '*');
        // Unsubscribing to prevent multiple sends if we receive more than one rta_ready
        window.removeEventListener('message', onMessage);
      }
    };
    window.addEventListener('message', onMessage);
  }
}

$(document).on('pjax:end', function preparePagePjaxEnd(event, xhr) {
  /**
   * On pjax end, we need to do a number of 'document ready' actions but not all (favicon has already
   * been set, datepicker js has been loaded if needed, etc.).
   */
  $(DATATABLES_SELECTOR).removeClass('hidden');

  // We don't want modal to be shown when consultation submit failed. xhr = null when using back/forward.
  let shouldConfirmLocalDraft = false;
  if (!xhr || xhr.status === 200) {
    shouldConfirmLocalDraft = true;
  }

  preparePageReady(shouldConfirmLocalDraft);

  const userSettings = selectManagedExpertById({ expert: store.getState().expert }, window.userId);
  rtaSettingsIframeInit(userSettings);
});
